import React from 'react';
import { useEditorStore } from '../../store/editorStore';
import { track, EVENTS } from '../../lib/mixpanel';
import { Button } from '../UI/Button';
import styles from './FilterControls.module.css';

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  displayValue?: string;
}

const Slider: React.FC<SliderProps> = ({ label, value, min, max, step, onChange, displayValue }) => (
  <div className={styles['slider-wrapper']}>
    <div className={styles['slider-header']}>
      <span>{label}</span>
      <span className={styles['slider-value']}>{displayValue ?? value}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className={styles['range-input']}
    />
  </div>
);

export const FilterControls: React.FC = () => {
  const { filters, setFilters, resetFilters } = useEditorStore();

  const hasChanges =
    filters.brightness !== 0 ||
    filters.contrast !== 0 ||
    filters.saturation !== 0 ||
    filters.grayscale;

  return (
    <div className={styles['container']}>
      <div className={styles['header']}>
        <h3 className={styles['title']}>Adjustments</h3>
        {hasChanges && (
          <Button variant="ghost" size="sm" onClick={() => { track(EVENTS.FILTER_RESET); resetFilters(); }}>
            Reset
          </Button>
        )}
      </div>

      <div className={styles['sliders-group']}>
        <Slider
          label="Exposure"
          value={filters.brightness}
          min={-1}
          max={1}
          step={0.05}
          onChange={(v) => setFilters({ brightness: v })}
          displayValue={filters.brightness > 0 ? `+${filters.brightness.toFixed(2)}` : filters.brightness.toFixed(2)}
        />

        <Slider
          label="Contrast"
          value={filters.contrast}
          min={-100}
          max={100}
          step={1}
          onChange={(v) => setFilters({ contrast: v })}
          displayValue={filters.contrast > 0 ? `+${filters.contrast}` : `${filters.contrast}`}
        />

        <Slider
          label="Saturation"
          value={filters.saturation}
          min={-2}
          max={2}
          step={0.05}
          onChange={(v) => setFilters({ saturation: v })}
          displayValue={filters.saturation > 0 ? `+${filters.saturation.toFixed(2)}` : filters.saturation.toFixed(2)}
        />

        <div className={styles['toggle-row']}>
          <span className={styles['toggle-label']}>Black & White</span>
          <button
            onClick={() => setFilters({ grayscale: !filters.grayscale })}
            className={[
              styles['toggle-track'],
              filters.grayscale ? styles['toggle-track-on'] : styles['toggle-track-off'],
            ].join(' ')}
          >
            <span
              className={[
                styles['toggle-knob'],
                filters.grayscale ? styles['toggle-knob-on'] : styles['toggle-knob-off'],
              ].join(' ')}
            />
          </button>
        </div>
      </div>
    </div>
  );
};
